import { notifications } from '@mantine/notifications';
import type { AxiosError } from 'axios';

// Type for API error response
interface ApiErrorResponse {
  error?: string;
  message?: string;
  errors?: Record<string, string[]>;
  status?: number;
}

// Type for handled error result
export interface ApiResult<T> {
  data?: T;
  error?: string;
  isError: boolean;
}

/**
 * Extracts a user-friendly error message from various error formats
 */
export const extractErrorMessage = (error: unknown): string => {
  // Handle Axios errors
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    
    if (axiosError.response?.data) {
      const data = axiosError.response.data;
      
      // Check for common error message fields
      if (data.error) return data.error;
      if (data.message) return data.message;
      
      // Handle validation errors
      if (data.errors) {
        const firstError = Object.values(data.errors)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          return firstError[0];
        }
      }
    }
    
    // Handle network errors
    if (axiosError.code === 'ECONNABORTED') {
      return 'Request timeout. Please try again.';
    }
    if (axiosError.code === 'ERR_NETWORK') {
      return 'Network error. Please check your connection.';
    }
    
    // Handle HTTP status codes
    switch (axiosError.response?.status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'You are not authorized. Please login again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 422:
        return 'Validation failed. Please check your input.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Request failed with status ${axiosError.response?.status}`;
    }
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Default message
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Shows an error notification to the user
 */
export const showErrorNotification = (error: unknown, title = 'Error') => {
  const message = extractErrorMessage(error);
  
  notifications.show({
    title,
    message,
    color: 'red',
    autoClose: 5000,
  });
};

/**
 * Wraps an async function with error handling
 * Returns a result object with data or error
 */
export async function withErrorHandling<T>(
  asyncFn: () => Promise<T>,
  options?: {
    showNotification?: boolean;
    notificationTitle?: string;
    rethrow?: boolean;
  }
): Promise<ApiResult<T>> {
  try {
    const data = await asyncFn();
    return { data, isError: false };
  } catch (error) {
    const errorMessage = extractErrorMessage(error);
    
    // Show notification if requested
    if (options?.showNotification !== false) {
      showErrorNotification(error, options?.notificationTitle);
    }
    
    // Log error for debugging
    console.error('API Error:', error);
    
    // Rethrow if requested
    if (options?.rethrow) {
      throw error;
    }
    
    return { error: errorMessage, isError: true };
  }
}

/**
 * Wraps an async function for use in React Query
 * Always throws errors for React Query to handle
 */
export async function withQueryErrorHandling<T>(
  asyncFn: () => Promise<T>
): Promise<T> {
  try {
    return await asyncFn();
  } catch (error) {
    // Log for debugging
    console.error('Query Error:', error);
    
    // Ensure error has a message
    if (error && typeof error === 'object' && !('message' in error)) {
      throw new Error(extractErrorMessage(error));
    }
    
    throw error;
  }
}

/**
 * Type guard to check if a result is an error
 */
export const isApiError = <T>(result: ApiResult<T>): result is ApiResult<T> & { error: string } => {
  return result.isError && result.error !== undefined;
};

/**
 * Retry configuration for API calls
 */
export const defaultRetryConfig = {
  retries: 3,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  retryCondition: (error: AxiosError) => {
    // Retry on network errors or 5xx errors
    return !error.response || (error.response.status >= 500 && error.response.status < 600);
  },
};